/**
 * VehicleStatusesPage - Cadastro de Status do veículo (menu "Veículos › Status Veículos")
 *
 * Status de sistema (Ativo/Cancelado) aparecem bloqueados; o tenant cria os próprios,
 * escolhendo se cada um conta como Ativo ou Cancelado. Mesmo modelo da tela de Origens.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-02 v1.0.0 - Criação inicial.
 */
import { useMemo, useState } from 'react';
import { CircleDot, Lock, MoreHorizontal, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import {
  ListPageLayout,
  ListPageHeader,
  ListPageFiltersBar,
  ListPageContent,
  LIST_FILTERS_INPUT_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  useDeleteVehicleStatus,
  useUpsertVehicleStatus,
  useVehicleStatuses,
  type VehicleBaseStatus,
  type VehicleStatus,
} from '@/hooks/useVehicleStatuses';
import { VehicleStatusBadge } from '@/components/vehicles/VehicleStatus';

interface EditorState {
  open: boolean;
  id: string | null;
  label: string;
  color: string;
  base_status: VehicleBaseStatus;
  sort_order: number;
  is_active: boolean;
}

const EMPTY: EditorState = { open: false, id: null, label: '', color: '#3B82F6', base_status: 'active', sort_order: 100, is_active: true };
const BASE_LABEL: Record<VehicleBaseStatus, string> = { active: 'Ativo', cancelled: 'Cancelado' };

export default function VehicleStatusesPage() {
  const { statuses, isLoading } = useVehicleStatuses(true);
  const upsert = useUpsertVehicleStatus();
  const remove = useDeleteVehicleStatus();
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<EditorState>(EMPTY);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('pt-BR');
    return statuses.filter((s) => !q || s.label.toLocaleLowerCase('pt-BR').includes(q));
  }, [statuses, search]);

  const openEdit = (s: VehicleStatus) =>
    setEditor({ open: true, id: s.id, label: s.label, color: s.color ?? '#3B82F6', base_status: s.base_status, sort_order: s.sort_order, is_active: s.is_active });

  /** Salva; nome sem HTML e limitado (o servidor valida de novo) */
  const handleSave = async () => {
    const label = editor.label.replace(/[<>]/g, '').trim();
    if (!label) return;
    await upsert.mutateAsync({ id: editor.id, label, color: editor.color, base_status: editor.base_status, sort_order: editor.sort_order, is_active: editor.is_active });
    setEditor(EMPTY);
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Status de Veículo"
        actions={(
          <Button size="xs" onClick={() => setEditor({ ...EMPTY, open: true })}>
            <Plus className="mr-1.5 h-4 w-4" /> Novo status
          </Button>
        )}
      />

      <ListPageFiltersBar className="flex-nowrap">
        <Input
          className={`${LIST_FILTERS_INPUT_CLASS} min-w-0 flex-1 sm:max-w-sm`}
          value={search}
          maxLength={60}
          placeholder="Buscar status..."
          onChange={(e) => setSearch(e.target.value)}
        />
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full rounded-lg" />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <CircleDot className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhum status encontrado.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead className="w-32">Conta como</TableHead>
                <TableHead className="w-28">Tipo</TableHead>
                <TableHead className="w-24">Situação</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id}>
                  <TableCell><VehicleStatusBadge statusId={s.id} /></TableCell>
                  <TableCell className="text-sm">{BASE_LABEL[s.base_status]}</TableCell>
                  <TableCell>
                    {s.is_system ? (
                      <Badge variant="secondary" className="h-5 gap-1 px-1.5 text-[10px]"><Lock className="h-3 w-3" /> Sistema</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Personalizado</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.is_active ? 'Ativo' : 'Inativo'}</TableCell>
                  <TableCell>
                    {/* Status de sistema não têm ações (somente leitura) */}
                    {!s.is_system && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(s)}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => upsert.mutate({ id: s.id, label: s.label, color: s.color, base_status: s.base_status, sort_order: s.sort_order, is_active: !s.is_active })}
                          >
                            <Power className="mr-2 h-4 w-4" /> {s.is_active ? 'Inativar' : 'Ativar'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" disabled={remove.isPending} onClick={() => remove.mutate(s.id)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      <Dialog open={editor.open} onOpenChange={(open) => !open && setEditor(EMPTY)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editor.id ? 'Editar status' : 'Novo status'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vs-label">Nome *</Label>
              <Input id="vs-label" autoFocus maxLength={60} value={editor.label} placeholder="Ex.: Em manutenção"
                onChange={(e) => setEditor((c) => ({ ...c, label: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Conta como</Label>
                <Select value={editor.base_status} onValueChange={(v) => setEditor((c) => ({ ...c, base_status: v as VehicleBaseStatus }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vs-color">Cor</Label>
                <Input id="vs-color" type="color" className="h-9 p-1" value={editor.color}
                  onChange={(e) => setEditor((c) => ({ ...c, color: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vs-order">Ordem de exibição</Label>
              <Input id="vs-order" type="number" min={0} max={9999} value={editor.sort_order}
                onChange={(e) => setEditor((c) => ({ ...c, sort_order: Math.max(0, Math.min(9999, parseInt(e.target.value, 10) || 0)) }))} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-sm">Ativo</Label>
                <p className="text-xs text-muted-foreground">Status inativos não aparecem para novos lançamentos.</p>
              </div>
              <Switch checked={editor.is_active} onCheckedChange={(v) => setEditor((c) => ({ ...c, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button size="xs" variant="outline" onClick={() => setEditor(EMPTY)}>Cancelar</Button>
            <Button size="xs" disabled={!editor.label.trim() || upsert.isPending} onClick={handleSave}>
              {upsert.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}
